// This file is automatically compiled by Webpack, along with any other files
// present in this directory. You're encouraged to place your actual application logic in
// a relevant structure within app/packs and only use these pack files to reference
// that code so it'll be compiled.
//
// To reference this file, add <%= javascript_pack_tag 'application' %> to the appropriate
// layout file, like app/views/layouts/application.html.erb

// Uncomment to copy all static images under ../images to the output folder and reference
// them with the image_pack_tag helper in views (e.g <%= image_pack_tag 'rails.png' %>)
// or the `imagePath` JavaScript helper below.
//
// const images = require.context('../images', true)
// const imagePath = (name) => images(name, true)

import 'bootstrap/dist/js/bootstrap'
import { createElement } from 'react'
import ReactDOM from 'react-dom'
import ujs from '@rails/ujs'
import UserProjects from '../src/components/user-projects'
import Navbar from '../src/components/ui/shared/navbar/navbar'
import ApiClient, { apiFetch } from '../src/api'
import TileServerClient from '../src/tile-server-client'
import Ts2Client from '../src/ts2-client'
import { StateProvider } from '../src/store'
import ErrorBoundaryCreateProject from '../src/components/create-project'
import bearing from '@turf/bearing'
import destination from '@turf/destination'

import './application.css'
import GlobalErrorBoundary from '../src/components/project-view/global-error-boundary'
import { loadStripe } from '@stripe/stripe-js'
import { enablePatches, enableMapSet } from 'immer'
import { matchesUA } from 'browserslist-useragent'
import { logMessage } from '../src/logger'
import { PaygPurchaseModal } from '../src/components/ui/shared/payg-purchase-modal'

// A lot of our old code expects a global jQuery
window.$ = window.jQuery = jQuery
ujs.start()

window.loadStripe = loadStripe

enablePatches()
enableMapSet()

window.solviApp = Object.assign(window.solviApp || {}, {
  navbar: function (container, props) {
    ReactDOM.render(<Navbar {...props} />, container)
  },
  userProjectList: function (container, props) {
    apiFetch(props.routesUrl).then(function (routes) {
      ReactDOM.render(
        createElement(UserProjects, {
          ...props,
          apiClient: new ApiClient(routes, props.authenticityToken),
        }),
        container
      )
    })
  },
  adminView: function (container, componentName, props) {
    import('../src/components/admin/components').then(
      ({ default: components }) => {
        const Component = components[componentName]

        if (!Component) {
          throw new Error(`Unknown admin component ${componentName}`)
        }

        return apiFetch(props.routesUrl).then(routes => {
          ReactDOM.render(
            <Component
              {...props}
              apiClient={new ApiClient(routes, props.authenticityToken)}
            />,
            container
          )
        })
      }
    )
  },
  showProject: function (container, projectProps) {
    renderProjectView(container, { ...projectProps, accessType: 'internal' })
  },
  publicMap: function (container, projectProps) {
    renderProjectView(container, { ...projectProps })
  },
  flightPlans: function (container, props) {
    import('../src/components/flight-plans-view').then(
      ({ default: FlightPlansView }) =>
        apiFetch(props.routesUrl).then(function (routes) {
          ReactDOM.render(
            <FlightPlansView
              {...props}
              apiClient={new ApiClient(routes, props.authenticityToken)}
            />,
            container
          )
        })
    )
  },
  farmsView: function (container, props) {
    import('../src/components/farms-view').then(({ default: FarmsView }) =>
      apiFetch(props.routesUrl).then(function (routes) {
        ReactDOM.render(
          <FarmsView
            {...props}
            apiClient={new ApiClient(routes, props.authenticityToken)}
          />,
          container
        )
      })
    )
  },
  createProject: function (container, props) {
    return apiFetch(props.routesUrl).then(routes => {
      ReactDOM.render(
        createElement(ErrorBoundaryCreateProject, {
          ...props,
          apiClient: new ApiClient(routes, props.authenticityToken),
        }),
        container
      )
    })
  },

  verifyBrowser(userAgent) {
    if (!matchesUA(userAgent, { allowHigherVersions: true })) {
      logMessage(`Old browser detected: ${userAgent}`)
    }
  },

  buyCredits: function (container, props) {
    return apiFetch(props.routesUrl).then(routes => {
      ReactDOM.render(
        createElement(PaygPurchaseModal, {
          ...props,
          apiClient: new ApiClient(routes, props.authenticityToken),
        }),
        container
      )
    })
  },
})

function renderProjectView(container, projectProps) {
  return import('../src/components/project-view/').then(
    ({ default: ProjectView }) =>
      apiFetch(projectProps.routesUrl).then(routes => {
        const apiClient = new ApiClient(
          routes,
          projectProps.authenticityToken,
          projectProps.accessToken
        )
        const tileServerClient = new TileServerClient(
          projectProps.ts2_url,
          projectProps.tileserverToken
        )
        const ts2Client = new Ts2Client(
          projectProps.ts2_url,
          projectProps.tileserverToken
        )

        ReactDOM.render(
          <GlobalErrorBoundary>
            <StateProvider
              {...projectProps}
              projectId={projectProps.id}
              apiClient={apiClient}
              tileServerClient={tileServerClient}
              ts2Client={ts2Client}
              mapboxToken={projectProps.mapbox_token}
              stripeToken={projectProps.stripeToken}
              showCase={projectProps.accessType === 'showcase'}
              shared={projectProps.accessType === 'shared'}
              editable={
                projectProps.accessType === 'internal' && projectProps.editable
              }>
              <ProjectView
                {...projectProps}
                apiClient={apiClient}
                tileServerClient={tileServerClient}
              />
            </StateProvider>
          </GlobalErrorBoundary>,
          container
        )
      })
  )
}

// TODO: These global cookie functions are legacy and should be refactored to something cleaner
// (but we need to figure out why are where they are used :)
window.createCookie = function createCookie(name, value, days) {
  var expires = ''
  if (days) {
    var date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    expires = '; expires=' + date.toUTCString()
  }
  document.cookie = name + '=' + value + expires + '; path=/'
}

window.readCookie = function readCookie(name) {
  var nameEQ = name + '='
  var ca = document.cookie.split(';')
  for (var i = 0; i < ca.length; i++) {
    var c = ca[i]
    while (c.charAt(0) === ' ') c = c.substring(1, c.length)
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
  }
  return null
}

window.eraseCookie = function eraseCookie(name) {
  window.createCookie(name, '', -1)
}

window.solviFlightPlanner = {
  getId: function () {
    return this.lastPlanId
  },
  getRoute: function () {
    const lastPlan = this.lastPlan
    return lastPlan && lastPlan.toGeoJson(true)
  },

  updateAppStatus: function (status) {
    if (this.currentPlanner) {
      this.currentPlanner.setState({ appStatus: status })
    }
  },

  // Given current position p and destination waypoint wp,
  // calculates an intermediate waypoint that is angle degrees
  // away from the straight line p->wp and dist kilometers away.
  intermediateWaypoint: function intermediateWaypoint(p, wp, angle, dist) {
    const wpBearing = bearing(p, wp)
    const intermediateBearing = wpBearing + angle
    return destination(p, dist, intermediateBearing)
  },
}
